import { Badge, Button, cn } from '@fruuit/ui'
import { RefreshCw, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react'
import { errorMessage } from '../../api/client'
import { formatDateTime } from '../records/format'
import { useAuditVerify } from './api'

const FAILURE_LABELS: Record<string, string> = {
  chain_mismatch: 'an event belongs to another chain',
  seq_gap: 'a sequence number is missing',
  prev_hash_mismatch: 'an event does not link to the one before it',
  hash_mismatch: 'an event was altered after it was written',
}

/**
 * Chain verification status (S15, P-AUD-003): recomputes every hash of the chain on the server
 * and shows the result, with a "Verify now" action.
 */
export function VerifyBadge({ chain, className }: { chain: string | null; className?: string }) {
  const verify = useAuditVerify(chain)
  const result = verify.data
  let badge = (
    <Badge tone="quiet" icon={<ShieldQuestion />}>
      {chain ? 'Not verified yet' : 'Choose a chain to verify'}
    </Badge>
  )
  let detail: string | null = null
  if (verify.isError) {
    badge = (
      <Badge tone="watch" icon={<ShieldQuestion />}>
        Verification unavailable
      </Badge>
    )
    detail = errorMessage(verify.error)
  } else if (result?.ok) {
    badge = (
      <Badge tone="positive" icon={<ShieldCheck />}>
        Chain verified
      </Badge>
    )
    detail = `${result.count.toLocaleString('en-GB')} events checked · ${formatDateTime(result.verified_at)}`
  } else if (result) {
    badge = (
      <Badge tone="attention" icon={<ShieldAlert />}>
        Verification failed
      </Badge>
    )
    detail = `At event #${result.failed_at ?? '?'}: ${FAILURE_LABELS[result.reason ?? ''] ?? result.reason ?? 'unknown reason'}.`
  }
  return (
    <div
      className={cn('flex flex-wrap items-center gap-3', className)}
      aria-live="polite"
      data-testid="verify-status"
    >
      {badge}
      {detail ? <span className="text-xs text-sage">{detail}</span> : null}
      <Button
        size="sm"
        variant="secondary"
        icon={<RefreshCw />}
        disabled={!chain}
        loading={verify.isFetching}
        onClick={() => void verify.refetch()}
      >
        Verify now
      </Button>
    </div>
  )
}
